import { useEffect, useState } from 'react';
import { fmtMoney, getLang, localeOf, useT } from '../lib/i18n';
import { alert, confirm, haptic } from '../lib/telegram';
import { PinnedSheet, SetRow } from '../components/SetSheet';

/**
 * Coach billing, in the coach app.
 *
 * Every billed coach sees their subscription in Settings and, once the 7
 * days of grace are over, a red banner (their own app only: clients see
 * nothing). A coach the owner switched off keeps a read-only calendar.
 * The owner alone gets the screen with every coach, where payments are
 * marked and a coach can be switched off or back on.
 */

export type Plan = 'monthly' | 'yearly' | 'founding';
export interface Billing {
  plan: Plan | null; monthlyPrice: number | null; yearPrice: number | null;
  paidUntil: string | null; daysLate: number; graceLeft: number; daysLeft: number | null;
  status: 'unbilled' | 'active' | 'grace' | 'overdue' | 'off';
  paymentDetails?: string; ownerName?: string;
  closedBy?: 'owner' | 'auto' | null; switchedOffAt?: string | null; lastSeenAt?: string | null;
  /** Days until access closes by itself; null when it won't. */
  closesInDays?: number | null;
  /** On a plan with a date and no payment recorded yet. */
  trial?: boolean;
  /** Whether a payment was ever marked: what tells a trial from a paid plan. */
  hasPaid?: boolean;
  /** The state the server worked out, by the same rule the bot uses. */
  state?: BillState;
  /** «Я оплатил» already sent for this period. */
  claimed?: boolean;
}

const BASE = import.meta.env.VITE_API_URL ?? '';
let authToken = '';
export const setBillingToken = (t: string) => { authToken = t; };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${authToken}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `http_${res.status}`);
  }
  return res.json();
}

/** "1 November" / "1 ноября", with the year when it is not this one. */
export function fmtDate(ymd: string) {
  const d = new Date(`${ymd}T12:00:00Z`);
  const sameYear = d.getUTCFullYear() === new Date().getUTCFullYear();
  return new Intl.DateTimeFormat(localeOf(getLang()), {
    timeZone: 'UTC', day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d).replace(/ г\.$/, '');
}

/** A year is ten months' price and lasts twelve. */
const addPeriod = (ymd: string, period: 'month' | 'year') => {
  const d = new Date(`${ymd}T12:00:00Z`);
  const day = d.getUTCDate();
  if (period === 'year') d.setUTCFullYear(d.getUTCFullYear() + 1);
  else {
    d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + 1);
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    d.setUTCDate(Math.min(day, last));
  }
  return d.toISOString().slice(0, 10);
};
const todayYmd = () => new Date().toISOString().slice(0, 10);

function StatusChip({ b }: { b: Billing }) {
  const t = useT();
  const [cls, text] = b.status === 'active' && billState(b) === 'trial' ? ['bill-chip--plan', t('bill.trialChip')]
    : b.status === 'active' ? ['bill-chip--good', t('bill.active')]
    : b.status === 'grace' ? ['bill-chip--warn', t('bill.grace', { n: b.graceLeft })]
    : b.status === 'overdue' ? ['bill-chip--bad', t('bill.overdue', { n: b.daysLate })]
    : b.status === 'off' ? ['bill-chip--off', t('bill.off')]
    : ['bill-chip--off', t('bill.unbilled')];
  return <span className={`bill-chip ${cls}`}>{text}</span>;
}

function planLabel(t: ReturnType<typeof useT>, plan: Plan | null, monthly: number | null) {
  if (!plan || monthly === null) return t('bill.noPlan');
  return plan === 'yearly' ? t('bill.planYearly', { price: fmtMoney(monthly * 10) })
    : plan === 'founding' ? t('bill.planFounding', { price: fmtMoney(monthly) })
    : t('bill.planMonthly', { price: fmtMoney(monthly) });
}

function HowToPay({ b, open: startOpen = false }: { b: Billing; open?: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(startOpen);
  return (
    <div className="bill-how">
      <button className="bill-how__toggle" onClick={() => { haptic.tap(); setOpen(!open); }}>
        {t('bill.howToPay')} {open ? '▴' : '▾'}
      </button>
      {open && (
        <>
          {/* The owner's own text, shown as written. */}
          <div className="bill-how__details">{b.paymentDetails || t('bill.noDetails')}</div>
          <div className="card__when">{t('bill.afterPay', { owner: b.ownerName || '' })}</div>
        </>
      )}
    </div>
  );
}

/** What one period costs this coach, with its unit: «49 €/мес», «790 €/год». */
function pricePer(t: ReturnType<typeof useT>, b: Billing) {
  return b.plan === 'yearly'
    ? t('bill.perYearP', { amount: fmtMoney(b.yearPrice ?? 0) })
    : t('bill.perMonthP', { amount: fmtMoney(b.monthlyPrice ?? 0) });
}
const planName = (t: ReturnType<typeof useT>, plan: Plan | null) =>
  plan === 'yearly' ? t('bill.nameYearly') : plan === 'founding' ? t('bill.nameFounding') : t('bill.nameMonthly');

/**
 * The three states every billing screen speaks in, worked out the same way
 * wherever they are: nothing paid yet is a trial, a date gone by is
 * overdue, and a coach who has paid and is in date is paid up. Until the
 * first payment there is no «Оплачено до» to show, so no screen says it.
 */
export type BillState = 'trial' | 'active' | 'overdue';
export function billState(b: Billing): BillState {
  // The server works it out (billStateOf, shared with the bot); the rest is
  // for a payload from before it did.
  if (b.state) return b.state;
  if (b.status === 'grace' || b.status === 'overdue') return 'overdue';
  return (b.hasPaid ?? !b.trial) ? 'active' : 'trial';
}

/** The state in one line: «Пробный период до 20 октября», «Просрочено с 20 октября». */
function stateTitle(t: ReturnType<typeof useT>, b: Billing, s: BillState) {
  if (!b.paidUntil) return t('bill.noDate');
  const date = fmtDate(b.paidUntil);
  return s === 'trial' ? t('bill.tTrial', { date })
    : s === 'overdue' ? t('bill.tOverdue', { date })
    : t('bill.tPaid', { date });
}

/** What is owed next, under the title. Overdue says it in the title itself. */
function stateSub(t: ReturnType<typeof useT>, b: Billing, s: BillState) {
  if (s === 'overdue' || !b.paidUntil || !b.plan || b.monthlyPrice === null) return null;
  const price = fmtMoney((b.plan === 'yearly' ? b.yearPrice : b.monthlyPrice) ?? 0);
  const date = fmtDate(b.paidUntil);
  return s === 'trial' ? t('bill.subTrial', { date, price }) : t('bill.subNext', { date, price });
}

/** The «Тариф» line: trial, paid, or waiting for payment. */
function planLine(t: ReturnType<typeof useT>, b: Billing) {
  const date = b.paidUntil ? fmtDate(b.paidUntil) : '';
  const s = billState(b);
  if (s === 'overdue') return t('bill.lineOverdue', { date });
  if (s === 'trial') return t('bill.lineTrial', { date, price: pricePer(t, b) });
  return t('bill.linePaid', { plan: planName(t, b.plan), price: pricePer(t, b), date });
}

/**
 * «Я оплатил» sent: the row, the sheet and the banner all know at once,
 * wherever they are on screen.
 */
const CLAIMED = 'coach:billing-claimed';
function useClaimed(b: Billing) {
  const [claimed, setClaimed] = useState(!!b.claimed);
  useEffect(() => { setClaimed(!!b.claimed); }, [b.claimed, b.paidUntil]);
  useEffect(() => {
    const on = () => setClaimed(true);
    window.addEventListener(CLAIMED, on);
    return () => window.removeEventListener(CLAIMED, on);
  }, []);
  return claimed;
}

/** The plan in full, the owner's payment details, and «Я оплатил». */
export function PlanSheet({ b, onClose }: { b: Billing; onClose: () => void }) {
  const t = useT();
  const claimed = useClaimed(b);
  const [busy, setBusy] = useState(false);
  async function claim() {
    setBusy(true);
    try {
      await api('/api/coach/billing/claim', { method: 'POST', body: '{}' });
      haptic.success();
      window.dispatchEvent(new Event(CLAIMED));
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }
  const s = billState(b);
  return (
    <PinnedSheet title={t('bill.row')} onClose={onClose}
      footer={claimed
        ? <button className="paybar__opt" onClick={onClose}>{t('bill.close')}</button>
        : <>
            <button className="paybar__opt" onClick={onClose}>{t('bill.close')}</button>
            <button className="savebtn" disabled={busy} onClick={claim}>{t('bill.iPaid')}</button>
          </>}>
      <div className="bill-sheet">
        <div className="bill-sheet__row"><span>{t('bill.sPlan')}</span><b>{planName(t, b.plan)}</b></div>
        <div className="bill-sheet__row"><span>{t('bill.sPrice')}</span><b>{pricePer(t, b)}</b></div>
        {b.paidUntil && (
          <div className="bill-sheet__row">
            <span>{s === 'trial' ? t('bill.sTrialUntil') : s === 'overdue' ? t('bill.sWaitingSince') : t('bill.sNext')}</span>
            <b>{fmtDate(b.paidUntil)}</b>
          </div>
        )}
        <div className="sheet__label" style={{ marginTop: 14 }}>{t('bill.sDetails')}</div>
        {/* The owner's own text, shown as written. */}
        <div className="bill-how__details">{b.paymentDetails || t('bill.noDetails')}</div>
        {claimed
          ? <div className="bill-sheet__thanks">{t('bill.thanks')}</div>
          : <div className="card__when" style={{ marginTop: 8 }}>{t('bill.afterPay', { owner: b.ownerName || '' })}</div>}
      </div>
    </PinnedSheet>
  );
}

/** Settings, first row: «Тариф» and its one-line status. Billed coaches only. */
export function PlanRow({ b, inline }: { b: Billing; /** Already inside a card: the row alone. */ inline?: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  if (!b.plan) return null;
  const row = <SetRow id="setup-plan" icon="💳" title={t('bill.row')} status={planLine(t, b)} onOpen={() => setOpen(true)} />;
  return (
    <>
      {inline ? row : <div className="list"><div className="card setrows">{row}</div></div>}
      {open && <PlanSheet b={b} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * The banner at the top of every coach screen, only when it matters:
 * a trial ending within 5 days, a paid period ending within 3, or a
 * payment overdue (until access closes by itself). Once «Я оплатил» is
 * sent it just says «Ожидаем подтверждения». Tapping opens the plan.
 * Switched off: the closed banner, as before.
 */
export function BillingBanner({ b }: { b: Billing }) {
  const t = useT();
  const claimed = useClaimed(b);
  const [open, setOpen] = useState(false);
  if (b.status === 'off') {
    return (
      <div className="bill-banner bill-banner--off">
        <div className="bill-banner__title">{t('bill.offTitle')}</div>
        <div className="bill-banner__body">{t('bill.offBody', { owner: b.ownerName || '' })}</div>
        <HowToPay b={b} />
      </div>
    );
  }
  if (!b.plan || !b.paidUntil) return null;
  const left = b.daysLeft ?? 99;
  const s = billState(b);
  const show = s === 'overdue' || (s === 'trial' ? left <= 5 : left <= 3);
  if (!show) return null;
  const date = fmtDate(b.paidUntil);
  const price = fmtMoney((b.plan === 'yearly' ? b.yearPrice : b.monthlyPrice) ?? 0);
  const [cls, title, body] = claimed
    ? ['bill-banner--wait', t('bill.bWaiting'), t('bill.thanks')]
    : s === 'overdue'
      ? ['', t('bill.bOverdue', { date }), b.closesInDays != null
          ? t('bill.bOverdueCloses', { n: b.closesInDays, days: t.word('bill.days', b.closesInDays) })
          : t('bill.bOverdueBody')]
      : s === 'trial'
        ? ['bill-banner--soon', t('bill.bTrial', { date }), t('bill.bTrialBody', { date, price })]
        : ['bill-banner--soon', t('bill.bPaid', { date }), t('bill.bPaidBody', { date, price })];
  return (
    <>
      <button className={`bill-banner bill-banner--tap ${cls}`} onClick={() => { haptic.tap(); setOpen(true); }}>
        <div className="bill-banner__title">{title}</div>
        <div className="bill-banner__body">{body}</div>
      </button>
      {open && <PlanSheet b={b} onClose={() => setOpen(false)} />}
    </>
  );
}

// ---------------------------------------------------------------------
//  The owner's screen
// ---------------------------------------------------------------------

interface CoachRow extends Billing {
  id: string; name: string; isOwner: boolean; since: string;
  clients: number; active: number; sessions: number;
}
interface Summary { perMonth: number; paid: number; grace: number; overdue: number; closed: number; clients: number; active: number }

const initials = (name: string) => name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

/** "today, 09:12" / "3 days ago" for when a coach last opened the app. */
function lastSeen(t: ReturnType<typeof useT>, iso: string | null | undefined) {
  if (!iso) return t('biz.never');
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  const time = new Intl.DateTimeFormat(localeOf(getLang()), { hour: '2-digit', minute: '2-digit' }).format(d);
  if (new Date().toDateString() === d.toDateString()) return t('biz.today', { time });
  if (days <= 1) return t('biz.yesterday');
  return t('biz.daysAgo', { n: days, days: t.word('bill.days', days) });
}

/** Most urgent first: overdue, grace, closed, then paid; the owner last. */
const ORDER: Record<string, number> = { overdue: 0, grace: 1, off: 2, active: 3, unbilled: 4 };

/**
 * The owner's «Бизнес» tab: income from coach plans, coaches by status,
 * clients across all coaches, then every coach. Counts only: no coach's
 * own income and no client payments, ever.
 */
export function Business() {
  const t = useT();
  const [data, setData] = useState<{ summary: Summary; coaches: CoachRow[]; paymentDetails: string; contactUsername?: string } | null>(null);
  const [contact, setContact] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [details, setDetails] = useState('');
  const load = () => api<{ summary: Summary; coaches: CoachRow[]; paymentDetails: string; contactUsername?: string }>('/api/owner/business')
    .then((d) => { setData(d); setDetails(d.paymentDetails); setContact(d.contactUsername ?? ''); })
    .catch(() => setData(null));
  useEffect(() => { load(); }, []);

  if (open) return <OwnerCoach id={open} onBack={() => { setOpen(null); load(); }} />;
  if (!data) return <div className="list"><div className="skeleton" style={{ height: 200 }} /></div>;
  const s = data.summary;
  const coaches = [...data.coaches].sort((a, b) =>
    Number(a.isOwner) - Number(b.isOwner) || (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || a.name.localeCompare(b.name));

  async function saveContact() {
    try {
      await api('/api/owner/contact', { method: 'PUT', body: JSON.stringify({ text: contact }) });
      haptic.success(); load();
    } catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
  }

  async function saveDetails() {
    try {
      await api('/api/owner/payment-details', { method: 'PUT', body: JSON.stringify({ text: details }) });
      haptic.success(); load();
    } catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
  }

  return (
    <div className="bill-owner">
      <div className="list" style={{ paddingTop: 12 }}>
        <div className="card biz-income">
          <span className="card__when">{t('biz.income')}</span>
          <b>{fmtMoney(s.perMonth)}</b>
          <span className="card__when">{t('biz.incomeSub')}</span>
        </div>
      </div>
      <div className="biz-kpis">
        {([['paid', s.paid, 'good'], ['grace', s.grace, 'warn'], ['overdue', s.overdue, 'bad'], ['closed', s.closed, 'off']] as const).map(([k, n, c]) => (
          <div key={k}><b className={`biz-k--${c}`}>{n}</b><span>{t(`biz.${k}`)}</span></div>
        ))}
      </div>
      <div className="list" style={{ paddingTop: 8 }}>
        <div className="card biz-clients">
          <span><span className="card__when" style={{ display: 'block' }}>{t('biz.clientsAll')}</span>
            <span className="card__when">{t('biz.activeAll', { n: s.active })}</span></span>
          <b>{s.clients}</b>
        </div>
      </div>
      <OwnerCatalogue />
      <div className="section-title biz-head"><span>{t('biz.coaches')}</span><span>{t('biz.urgentFirst')}</span></div>
      <div className="list">
        <div className="card bill-list">
          {coaches.map((c) => (
            <button key={c.id} className={`bill-row biz-row${c.status === 'off' ? ' bill-row--off' : ''}`}
              disabled={c.isOwner} onClick={() => { haptic.tap(); setOpen(c.id); }}>
              <span className="bill-face">{initials(c.name)}</span>
              <span className="bill-row__main">
                <span className="bill-row__top">
                  <b>{c.name}{c.isOwner ? ` ${t('bill.you')}` : ''}</b>
                  {!c.isOwner && <StatusChip b={c} />}
                </span>
                <span className="biz-line">
                  {c.isOwner ? t('bill.ownerRow') : <>
                    {planLabel(t, c.plan, c.monthlyPrice)}
                    {c.paidUntil && <span className="card__when"> · {t('bill.to', { date: fmtDate(c.paidUntil) })}</span>}
                    {c.closesInDays != null && <span className="biz-bad"> · {t('biz.closesIn', { n: c.closesInDays, days: t.word('bill.days', c.closesInDays) })}</span>}
                    {c.status === 'off' && c.closedBy === 'auto' && <span className="card__when"> · {t('biz.autoClosed')}</span>}
                  </>}
                </span>
                <span className="biz-meta">
                  <span><b>{c.clients}</b> {t.word('biz.clientsW', c.clients)} · <b>{c.active}</b> {t.word('biz.activeW', c.active)}</span>
                  <span>{t('biz.members')} —</span>
                  <span><b>{c.sessions}</b> {t.word('biz.sessionsW', c.sessions)}</span>
                  {!c.isOwner && <span>{t('biz.lastSeen', { when: lastSeen(t, c.lastSeenAt) })}</span>}
                </span>
              </span>
              {!c.isOwner && <span className="bill-row__go">›</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="section-title">{t('bill.detailsTitle')}</div>
      <div className="list">
        <div className="card">
          <textarea className="notearea" rows={4} value={details} placeholder={t('bill.detailsPlaceholder')}
            onChange={(e) => setDetails(e.target.value)} />
          {details !== data.paymentDetails && <button className="savebtn" onClick={saveDetails}>{t('co.set.save')}</button>}
          <div className="card__when">{t('bill.detailsHint')}</div>
        </div>
      </div>
      {/* «Напишите нам» on coach sign-up links here. */}
      <div className="section-title">{t('biz.contactTitle')}</div>
      <div className="list">
        <div className="card">
          <input className="notearea" value={contact} placeholder={t('biz.contactPh')} maxLength={64}
            onChange={(e) => setContact(e.target.value)} />
          {contact.replace(/^@/, '') !== (data.contactUsername ?? '') && <button className="savebtn" onClick={saveContact}>{t('co.set.save')}</button>}
          <div className="card__when">{t('biz.contactHint')}</div>
        </div>
      </div>
    </div>
  );
}

interface CoachDetail extends Billing {
  id: string; name: string; since: string; clients: number; active: number; sessions: number;
  payments: { period: 'month' | 'year'; amount: string; paid_until_before: string | null; paid_until_after: string; created_at: string }[];
}

function OwnerCoach({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useT();
  const [d, setD] = useState<CoachDetail | null>(null);
  const [price, setPrice] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => api<CoachDetail>(`/api/owner/coaches/${id}`).then((r) => {
    setD(r); setPrice(r.monthlyPrice !== null ? String(r.monthlyPrice) : ''); setDate(r.paidUntil ?? '');
  });
  useEffect(() => { load(); }, [id]);
  if (!d) return <div className="list"><div className="skeleton" style={{ height: 240 }} /></div>;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try { await fn(); haptic.success(); await load(); }
    catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  }
  const setPlan = (plan: Plan) => run(() => api(`/api/owner/coaches/${id}/plan`, {
    method: 'PUT', body: JSON.stringify({ plan, monthlyPrice: plan === 'founding' ? 49 : 79 }),
  }));
  const savePrice = () => run(() => api(`/api/owner/coaches/${id}/plan`, {
    method: 'PUT', body: JSON.stringify({ plan: d.plan, monthlyPrice: Number(price) }),
  }));
  const saveDate = () => run(() => api(`/api/owner/coaches/${id}/plan`, {
    method: 'PUT', body: JSON.stringify({ paidUntil: date || null }),
  }));
  const base = d.paidUntil ?? todayYmd();
  const state = billState(d);
  const sub = stateSub(t, d, state);
  async function markPaid(period: 'month' | 'year') {
    const amount = period === 'year' ? d!.yearPrice! : d!.monthlyPrice!;
    const to = addPeriod(base, period);
    if (!(await confirm(t('bill.confirmPaid', { name: d!.name, amount: fmtMoney(amount), date: fmtDate(to) })))) return;
    await run(() => api(`/api/owner/coaches/${id}/paid`, { method: 'POST', body: JSON.stringify({ period }) }));
  }
  async function switchOff(off: boolean) {
    if (!(await confirm(off ? t('bill.confirmOff', { name: d!.name }) : t('bill.confirmOn', { name: d!.name })))) return;
    await run(() => api(`/api/owner/coaches/${id}/switch`, { method: 'PUT', body: JSON.stringify({ off }) }));
  }

  const plans: [Plan, string, string][] = [
    ['monthly', t('bill.monthly'), fmtMoney(79)],
    ['yearly', t('bill.yearly'), fmtMoney(790)],
    ['founding', t('bill.founding'), t('bill.perMo', { price: fmtMoney(d.plan === 'founding' ? d.monthlyPrice ?? 49 : 49) })],
  ];

  return (
    <div className="bill-owner" data-layer>
      <button className="bill-back" data-back onClick={() => { haptic.tap(); onBack(); }}>‹ {t('biz.title')}</button>
      <div className="bill-head">
        <span className="bill-face bill-face--big">{initials(d.name)}</span>
        <span><span className="bill-h1" style={{ padding: 0 }}>{d.name}</span>
          <span className="card__when" style={{ display: 'block' }}>{t('bill.since', { date: fmtDate(String(d.since).slice(0, 10)) })}</span></span>
        <StatusChip b={d} />
      </div>

      {d.status === 'off' ? (
        <div className="list" style={{ paddingTop: 12 }}>
          <div className="card__when" style={{ paddingBottom: 8 }}>
            {d.closedBy === 'auto' ? t('biz.closedAuto', { date: fmtDate(String(d.switchedOffAt).slice(0, 10)) })
              : t('biz.closedOwner', { date: fmtDate(String(d.switchedOffAt).slice(0, 10)) })}
          </div>
          <button className="bill-btn bill-btn--open" style={{ width: '100%' }} disabled={busy} onClick={() => switchOff(false)}>{t('biz.open')}</button>
          <div className="card__when" style={{ textAlign: 'center', marginTop: 6 }}>{t('biz.openNote')}</div>
        </div>
      ) : d.closesInDays != null && (
        <div className="biz-warn">{t('biz.closesNotice', { n: d.closesInDays, days: t.word('bill.days', d.closesInDays) })}</div>
      )}

      <div className="biz-stats">
        <div><b>{d.clients}</b><span>{t.word('biz.clientsW', d.clients)}</span></div>
        <div><b>{d.active}</b><span>{t.word('biz.activeW', d.active)}</span></div>
        <div><b>{d.sessions}</b><span>{t.word('biz.sessionsW', d.sessions)}</span></div>
      </div>
      <div className="card__when" style={{ padding: '6px 20px 0' }}>
        {t('biz.lastSeen', { when: lastSeen(t, d.lastSeenAt) })} · {t('biz.membersSoon')}
      </div>

      <div className="section-title">{t('bill.plan')}</div>
      <div className="bseg" style={{ marginTop: 0 }} role="radiogroup">
        {plans.map(([p, label, sub]) => (
          <button key={p} role="radio" aria-checked={d.plan === p} disabled={busy}
            onClick={() => { if (d.plan !== p) { haptic.tap(); void setPlan(p); } }}>
            {label}<small>{sub}</small>
          </button>
        ))}
      </div>
      {d.plan && (
        <div className="list">
          <div className="bill-field">
            <span className="card__when">{t('bill.monthlyPrice')}</span>
            <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
            <span>€</span>
            {price !== String(d.monthlyPrice) && <button className="balance__action" disabled={busy} onClick={savePrice}>{t('co.set.save')}</button>}
          </div>
          <div className="card__when" style={{ padding: '4px 4px 0' }}>{t('bill.yearNote', { year: fmtMoney(Number(price || 0) * 10) })}</div>
        </div>
      )}

      {/* Trial, paid or overdue — the date alone would say «Оплачено до» for all three. */}
      <div className="section-title">{t('bill.title')}</div>
      <div className="list">
        <div className="card">
          <div className={`bill-big${state === 'overdue' ? ' bill-big--bad' : ''}`}>{stateTitle(t, d, state)}</div>
          {sub && <div className="card__when">{sub}</div>}
          {d.status === 'grace' && <div className="bill-warn">{t('bill.graceLine', { late: d.daysLate, left: d.graceLeft })}</div>}
          {d.status === 'overdue' && <div className="bill-bad">{t('bill.overdueLine', { n: d.daysLate })}</div>}
          <div className="bill-field" style={{ marginTop: 10 }}>
            <span className="card__when">{state === 'trial' ? t('bill.sTrialUntil') : t('bill.paidUntil')}</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            {date !== (d.paidUntil ?? '') && <button className="balance__action" disabled={busy} onClick={saveDate}>{t('co.set.save')}</button>}
          </div>
        </div>
      </div>

      {d.plan && d.monthlyPrice !== null && (
        <>
          <div className="section-title">{t('bill.markPaid')}</div>
          <div className="bill-pay">
            <button className="bill-btn bill-btn--main" disabled={busy} onClick={() => markPaid('month')}>
              {t('bill.plusMonth', { amount: fmtMoney(d.monthlyPrice) })}
            </button>
            <button className="bill-btn bill-btn--soft" disabled={busy} onClick={() => markPaid('year')}>
              {t('bill.plusYear', { amount: fmtMoney(d.yearPrice ?? d.monthlyPrice * 10) })}
            </button>
          </div>
          <div className="card__when" style={{ padding: '6px 20px 0' }}>
            {t('bill.becomes', { date: fmtDate(addPeriod(base, d.plan === 'yearly' ? 'year' : 'month')) })}
          </div>
        </>
      )}

      <div className="section-title">{t('bill.history')}</div>
      <div className="list">
        <div className="card bill-list">
          {d.payments.length === 0 ? <div className="card__when" style={{ padding: '10px 0' }}>{t('bill.noPayments')}</div>
            : d.payments.map((p, i) => (
              <div key={i} className="bill-hist">
                <span className="card__when">{fmtDate(String(p.created_at).slice(0, 10))}</span>
                <span>{p.period === 'year' ? t('bill.plusYear', { amount: fmtMoney(Number(p.amount)) }) : t('bill.plusMonth', { amount: fmtMoney(Number(p.amount)) })}</span>
                <span className="card__when">{t('bill.to', { date: fmtDate(p.paid_until_after) })}</span>
              </div>
            ))}
        </div>
      </div>

      <div className="list" style={{ paddingTop: 18 }}>
        {d.status !== 'off' && <>
          <button className="bill-btn bill-btn--danger" style={{ width: '100%' }} disabled={busy} onClick={() => switchOff(true)}>{t('biz.close')}</button>
          <div className="card__when" style={{ textAlign: 'center', marginTop: 6 }}>{t('biz.closeNote')}</div>
        </>}
      </div>
    </div>
  );
}

interface UpcomingSession { id: string; starts_at: string; title: string; bookings: { name: string; lateCancel?: boolean }[] }

/**
 * A closed coach sees only this: why, how to pay, and a read-only list of
 * the sessions already booked. Nothing can be changed (the server refuses
 * it too).
 */
export function ClosedScreen({ b }: { b: Billing }) {
  const t = useT();
  const [list, setList] = useState<UpcomingSession[] | null>(null);
  useEffect(() => {
    const from = new Date().toISOString().slice(0, 10);
    const to = new Date(Date.now() + 60 * 86_400_000).toISOString().slice(0, 10);
    api<UpcomingSession[]>(`/api/coach/schedule?from=${from}&to=${to}`)
      .then((r) => setList(r.filter((x) => new Date(x.starts_at).getTime() > Date.now()
        && x.bookings.some((bk) => !bk.lateCancel))))
      .catch(() => setList([]));
  }, []);
  const fmt = (iso: string) => new Intl.DateTimeFormat(localeOf(getLang()), {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
  return (
    <>
      <BillingBanner b={b} />
      <div className="section-title">{t('biz.upcomingTitle')}</div>
      <div className="list">
        {list === null ? <div className="skeleton" />
          : list.length === 0 ? <div className="card__when" style={{ padding: '6px 4px' }}>{t('biz.upcomingNone')}</div>
          : (
            <div className="card bill-list">
              {list.map((x) => (
                <div key={x.id} className="bill-hist">
                  <span style={{ width: 132, flex: 'none' }}>{fmt(x.starts_at)}</span>
                  <span style={{ flex: 1 }}>{x.bookings.filter((bk) => !bk.lateCancel).map((bk) => bk.name).join(', ')}</span>
                </div>
              ))}
            </div>
          )}
        <div className="card__when" style={{ padding: '8px 4px' }}>{t('biz.upcomingNote')}</div>
      </div>
    </>
  );
}

/**
 * The catalogue at a glance for the owner: entries, coaches sharing or
 * not, the last «Упражнение месяца», and «Заполнить каталог из моей
 * библиотеки» (the owner's own drills into the catalogue).
 */
function OwnerCatalogue() {
  const t = useT();
  const [c, setC] = useState<{ entries: number; sharing: number; not_sharing: number;
    lastMonth: { month: string; name_ru: string; name_en: string; players: number; improved: number; coaches: number } | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api<any>('/api/owner/catalogue').then(setC).catch(() => setC(null));
  useEffect(() => { load(); }, []);
  async function seed() {
    setBusy(true);
    try {
      const r = await api<{ linked: number; created: number }>('/api/owner/catalogue/seed', { method: 'POST', body: '{}' });
      haptic.success(); await alert(t('biz.catSeeded', { linked: r.linked, created: r.created })); load();
    } catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  }
  if (!c) return null;
  const m = c.lastMonth;
  const monthLabel = m ? new Intl.DateTimeFormat(getLang() === 'ru' ? 'ru-RU' : 'en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${m.month}T12:00:00Z`)).replace(/ г\.$/, '') : '';
  return (
    <div className="list" style={{ paddingTop: 8 }}>
      <div className="card biz-cat">
        <span className="card__when">{t('biz.cat')}</span>
        <div className="biz-cat__row"><span>{t('biz.catEntries')}</span><b>{c.entries}</b></div>
        <span className="card__when">{t('biz.catSharing', { n: c.sharing, m: c.not_sharing })}</span>
        <span className="card__when">{m ? t('biz.catMonth', { month: monthLabel, name: getLang() === 'ru' ? m.name_ru : m.name_en, k: m.improved, m: m.players, n: m.coaches }) : t('biz.catMonthNone')}</span>
        <button className="paybar__opt" disabled={busy} onClick={seed}>{busy ? t('biz.catSeeding') : t('biz.catSeed')}</button>
      </div>
    </div>
  );
}
